import { useContext, useEffect, useState } from "react"

import { Box, Button, Container, MobileStepper, Typography } from '@mui/material'
import { useTranslation } from "react-i18next"

import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Spacer from "../../../atoms/Spacer/Spacer"
import { v4 as uuidv4 } from 'uuid'

import CustomSelect, { CustomSelectOption } from '../../../atoms/CustomSelect/CustomSelect'

import { logLandingPageAction } from "../../../../clients/logger"
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftCard.module.css'
import SubscriptionGiftStepperStep from "../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper"
import useGoogleTagManager from "../../../../helpers/analytics/use-google-tag-manager"
import GtmEventName from "../../../../helpers/analytics/GtmEventName"
import { Route } from "../../../../router"
import { useNavigate } from "react-router-dom"
import SwipeableViews from "react-swipeable-views"
import { useSubscriptionGiftRepository } from "../../../../repositories/SubscriptionGift/use-subscription-gift-repository"
import { AppContext } from "../../../../stores/App/AppStore"
import { UserContext } from "../../../../stores/User/UserStore"
import moment from "moment"

type CustomSelectValue = string | number | undefined

const SubscriptionGiftCard = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()
    const { retrieveGiftCards } = useSubscriptionGiftRepository()
    const { state: appState } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)
    const [recipientMessage, setRecipientMessage] = useState(``)
    const [activeStep, setActiveStep] = useState(0)
    const [giftPeriod, setGiftPeriod] = useState<CustomSelectValue>(0)
    const [validationError, setValidationError] = useState(false)
    const maxSteps = appState.giftCards.length
    const dateOfBirthMoment = moment(userState.subscriptionGift?.dateOfBirth, `DDMMYYYY`)
    const validationErrorMessage = `Please select a subscription period`

    useEffect(() => {
        retrieveGiftCards()
    }, [])

    const handlePreviousStepClick = () => {
        setActiveStep(Math.max(activeStep - 1, 0))
    }
    const handleNextStepClick = () => {
        // eslint-disable-next-line no-magic-numbers
        setActiveStep(Math.min(activeStep + 1, maxSteps - 1))
    }

    const subscriptionPeriodOptions = () => {
        const options: CustomSelectOption[] = []
        if (!dateOfBirthMoment.isValid()) {
            logLandingPageAction(`User tried to access Subscription Gift Details Form without a valid DOB`, `myInfo`)
            navigate(Route.HOME)
            return []
        }
        const dateOfBirthCopy = moment(dateOfBirthMoment)
        // eslint-disable-next-line no-magic-numbers
        const userMaxAgeDate = dateOfBirthCopy.add(25, `years`)
        let maxNumberOfYears = userMaxAgeDate.diff(moment(), `years`)
        if (!moment().isSameOrAfter(dateOfBirthCopy.clone().add(maxNumberOfYears, `years`))) {
            maxNumberOfYears += 1
        }
        // eslint-disable-next-line no-magic-numbers
        if (maxNumberOfYears > 7){
            return []
        }
        const localCopy = [...Array(maxNumberOfYears).keys()].map(i => i + 1)
        localCopy.forEach((i) => options.push({ label: i.toString(), value: i, id: i.toString() }))
        return options
    }

    const navigate = useNavigate()

    useTrackPageView(`Subscription Gift Card Page`)

    const handleSubmit = () => {
        if (Number(giftPeriod) > 0){
            trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_CARD_SELECTED)
            logLandingPageAction(`User pressed next on gift card selection`, `myInfo`)
            userState.subscriptionGift = {
                ...userState.subscriptionGift,
                giftCardTitle: appState.giftCards[activeStep].title,
                subscriptionPeriod: Number(giftPeriod),
                recipientMessage,
                code: uuidv4(),
            }
            navigate(`${Route.SUBSCRIPTION_GIFT_SENDER_DETAILS}`)
        }else {
            setValidationError(true)
        }
    }

    return (
        <Container>
            <CommonBackButton />
            <Spacer height={24} />
            <SubscriptionGiftStepperStep activeStep={1}/>
            <Spacer height={36} />
            <Typography variant="h3">{t(`subscription_gift_gift_card_header`)}</Typography>
            <Spacer height={12} />
            <Typography variant="h4">{t(`subscription_gift_gift_card_subheader`)}</Typography>
            <Spacer height={24} />
            <Typography variant="h3">{t(`subscription_gift_gift_card_subtitle`)}</Typography>
            <CustomSelect
                options={subscriptionPeriodOptions()}
                label={t(`subscription_gift_details_subscription_period`)}
                placeholder={t(`common_placeholder`)}
                onChange={setGiftPeriod}
                value={giftPeriod}
                isSignUp={false}/>
            <Spacer height={36} />
            <Typography variant="h3" className={styles.choose_gift_card_header}>{t(`subscription_gift_choose_gift_card`)}</Typography>
            <Spacer height={12} />
            {maxSteps > 0 && <Box className={styles.container}>
                <SwipeableViews enableMouseEvents index={activeStep}>
                    {appState.giftCards.map((giftCard) => {
                        return (
                            <Box display="flex" key={giftCard.title} justifyContent="center" alignItems="center">
                                <Box
                                    component="img"
                                    src={giftCard.giftCard}/>
                            </Box>
                        )
                    })
                    }
                </SwipeableViews>
                <MobileStepper
                    className={styles.mobileStepper}
                    steps={maxSteps}
                    position={`static`}
                    activeStep={activeStep}
                    nextButton={<ChevronRightIcon color='primary' onClick={handleNextStepClick} />}
                    backButton={<ChevronLeftIcon color='primary' onClick={handlePreviousStepClick} />}
                />
            </Box>}
            <Spacer height={24} />
            <Typography variant="h6">{t(`subscription_gift_recipient_message`)}</Typography>
            <Spacer height={12} />
            <textarea name="postContent"
                rows={6} cols={40}
                maxLength={300}
                value={recipientMessage}
                onChange={value => setRecipientMessage(value.target.value)} />
            <Spacer height={24} />
            {validationError && <Typography className={styles.validationError}>{validationErrorMessage}</Typography>}
            <Button variant='contained' onClick={handleSubmit} fullWidth>{t(`next`)}</Button>
        </Container>
    )
}

export default SubscriptionGiftCard